/**
 * AutopilotHealthCard — Sprint 7 · Project Symphony
 * The "Autopilot Health" always-visible strip.
 */
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import type { AutopilotDashboard } from "../api";

export function AutopilotHealthCard({
  dashboard,
}: {
  dashboard: AutopilotDashboard;
}): JSX.Element {
  const {
    state,
    trust_label,
    health_indicator,
    health_label,
    campaigns_running,
    pending_reviews,
    blocked_tasks,
    last_action,
    last_action_at,
    last_action_reason,
    next_scheduled_task,
    next_scheduled_at,
  } = dashboard;

  return (
    <Card className="p-6" data-testid="autopilot-health-card">
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Marketing Department
          </p>
          <div className="flex items-baseline gap-3">
            <span className="text-2xl" aria-hidden>{health_indicator}</span>
            <span
              data-testid="autopilot-health-label"
              className="text-2xl sm:text-3xl font-semibold"
            >
              {health_label}
            </span>
            <Badge variant="outline" data-testid="autopilot-state-badge">
              {state}
            </Badge>
            <Badge variant="secondary" data-testid="autopilot-trust-badge">
              {trust_label}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {campaigns_running} campaign(s) running · {pending_reviews} pending
            review(s) · {blocked_tasks} blocked task(s)
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:w-96">
          <div data-testid="autopilot-last-action">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
              Last Action
            </p>
            <p className="text-sm font-medium">{last_action}</p>
            {last_action_at && (
              <p className="text-xs text-muted-foreground">
                {new Date(last_action_at).toLocaleString()}
              </p>
            )}
            {last_action_reason && (
              <p className="text-xs text-muted-foreground mt-1 italic">
                {last_action_reason}
              </p>
            )}
          </div>
          <div data-testid="autopilot-next-task">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
              Next Scheduled Task
            </p>
            <p className="text-sm font-medium">{next_scheduled_task}</p>
            {next_scheduled_at && (
              <p className="text-xs text-muted-foreground">
                {new Date(next_scheduled_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
